import { Box, Stack, Typography } from '@mui/material';
import useListQuery, { DEFAULT_PAGE_SIZE, exportParams } from '../../hooks/useListQuery';
import { formatDay } from '../../utils/dates';
import { resolveRange } from '../filters/dateRanges';
import { DateRangeFilter, FilterBar, SearchFilter } from '../filters/Filters';
import DataTable from './DataTable';
import DownloadMenu from './DownloadMenu';

const NOT_FILTERS = ['page', 'page_size', 'ordering'];

/**
 * Title, filters and table of one list. filters({ query, setQuery }) renders the filter pills; the date range and,
 * with searchPlaceholder, search (the search query parameter) go on the row below. columns may be a
 * function of the list ({ patchRow, reload }) for cells with actions. exportPath (one of api/ads EXPORTS) adds a
 * download of every row matching the filters, at the table's top right; actions(list) renders more buttons beside it.
 */
export default function ListPage({
  title, description, fetchList, defaults, filters, searchPlaceholder, columns, onRowClick, emptyTitle, emptyHint,
  minWidth, exportPath, actions,
}) {
  const list = useListQuery(fetchList, defaults);
  const { query, setQuery } = list;
  const filterKeys = Object.keys(query).filter((key) => !NOT_FILTERS.includes(key) && query[key] !== defaults[key]);
  const { since, until } = resolveRange(query);
  const tableColumns = typeof columns === 'function' ? columns(list) : columns;

  const clear = () => setQuery(Object.fromEntries(filterKeys.map((key) => [key, null])));

  return (
    <Box component="section">
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
             alignItems={{ xs: 'flex-start', sm: 'flex-end' }} spacing={1} sx={{ mb: 2.5 }}>
        <Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>{title}</Typography>
          {description && <Typography color="text.secondary">{description}</Typography>}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          Metrics for {formatDay(since)} – {formatDay(until)}
        </Typography>
      </Stack>

      <FilterBar active={filterKeys.length > 0} onClear={clear}
                 date={<DateRangeFilter query={query} onChange={setQuery} />} search={searchPlaceholder && (
        <SearchFilter value={query.search} onChange={(search) => setQuery({ search })}
                      placeholder={searchPlaceholder} />
      )}>
        {filters({ query, setQuery })}
      </FilterBar>

      {(actions || exportPath) && (
        <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center"
               sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
          {actions && actions(list)}
          {exportPath && (
            <DownloadMenu path={exportPath} params={exportParams(query)} disabled={!list.count} label={title} />
          )}
        </Stack>
      )}

      <DataTable
        label={title} columns={tableColumns} rows={list.rows} count={list.count} loading={list.loading}
        error={list.error} page={Number(query.page) || 1} pageSize={Number(query.page_size) || DEFAULT_PAGE_SIZE}
        ordering={query.ordering} onOrderingChange={(ordering) => setQuery({ ordering })}
        onPageChange={(page) => setQuery({ page })} onPageSizeChange={(size) => setQuery({ page_size: size })}
        onRowClick={onRowClick && ((row) => onRowClick(row, query))} minWidth={minWidth}
        emptyTitle={filterKeys.length ? 'No results match these filters' : emptyTitle}
        emptyHint={filterKeys.length ? 'Try clearing or changing the filters.' : emptyHint}
      />
    </Box>
  );
}
